import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { clientApi, ApiError } from '../lib/api';
import { shouldWarnClientBeforeUnload, warnBeforeUnload } from '../lib/beforeUnload';
import type { PublicScript } from '../../shared/types';
import { Editor, type SelectionInfo, type TiptapEditor } from '../components/Editor/Editor';
import type { ThreadAnchor } from '../components/Editor/CommentDecorations';
import Wordmark from '../components/Wordmark';
import BundleScriptPicker from '../components/BundleScriptPicker';
import Eyebrow from '../components/Eyebrow';
import Button from '../components/Button';
import { useForceLight } from '../lib/theme';
import SelectionPill from '../components/SelectionPill';
import CommentSidebar from '../components/CommentSidebar';
import ClientNotesPanel, { type ClientNotesHandle } from '../components/ClientNotesPanel';
import RuntimeBadge from '../components/Editor/RuntimeBadge';
import ReadThrough from '../components/ReadThrough';
import RefImageLightbox from '../components/RefImageLightbox';
import { useRefImageSource } from '../lib/refImageSource';
import ClientReviewCard from '../components/ClientReviewCard';
import ReviewChecklistCard from '../components/ReviewChecklistCard';
import ShootViewCard from '../components/ShootViewCard';
import ClientOnboardingTour from '../components/ClientOnboardingTour';
import { hasSeenOnboarding } from '../lib/onboardingSeen';
import SuggestModeTransition, {
  type SuggestTransitionPhase,
  type SuggestTransitionVariant,
} from '../components/SuggestModeTransition';
import ProgressTracker from '../components/ProgressTracker';
import WhatChangedBanner from '../components/WhatChangedBanner';
import Toaster, { showToast } from '../components/Toaster';
import ContactLine from '../components/ContactLine';
import ContactModal from '../components/ContactModal';
import { useScriptComments } from '../lib/useScriptComments';
import { useClientSuggestion, type SuggestSaveState } from '../lib/useClientSuggestion';
import { extractScriptRows, hasShootView, shootableRows } from '../../shared/scriptRows';
import { hasRoundSummary } from '../../shared/roundSummary';
import { downloadBriefPdf, downloadScriptPdf } from '../lib/downloadPdf';
import { openShootView } from '../lib/openShootView';
import { isBriefTemplate } from '../../shared/briefFields';
import { parseDocJson } from '../lib/parseDoc';
import { useBundleReview, type BundleReviewContextValue } from '../lib/bundleReviewContext';

export default function ClientReview() {
  useForceLight();
  const { slug } = useParams<{ slug: string }>();
  // Reference images (2B) load through this link, gated by the worker.
  useRefImageSource(slug ? { kind: 'client', slug } : null);
  // Present only when mounted inside a bundle (BundleScriptView provides
  // it) — null on the standalone /s/:slug route, where this page must render
  // pixel-identical to before.
  const bundleReview = useBundleReview();
  const navigate = useNavigate();
  const [script, setScript] = useState<PublicScript | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editor, setEditor] = useState<TiptapEditor | null>(null);

  // Selection + comment state
  const [selection, setSelection] = useState<SelectionInfo | null>(null);
  const [selectionRect, setSelectionRect] = useState<DOMRect | null>(null);
  // The selection a comment is being written about, plus the document it was
  // made against: those versions travel with the comment so the server can
  // refuse it if the document moved while the composer was open.
  const [draftAnchor, setDraftAnchor] = useState<{
    from: number;
    to: number;
    text: string;
    doc_version: number;
    suggestion_id: string | null;
    suggestion_edit_version: number | null;
    /** The server refused these positions; the text must be selected again. */
    stale?: boolean;
  } | null>(null);
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);

  // Suggest mode
  const [mode, setMode] = useState<'review' | 'suggest'>('review');
  const suggestion = useClientSuggestion(slug);

  // Full-viewport transition overlay that covers the enter/exit swap — see
  // enterSuggestMode/exitSuggestMode below. null while idle; the timeout
  // that flips 'out' → null is tracked so it can be cleared if the page
  // unmounts mid-transition (e.g. the client navigates away while it's
  // still covering the page).
  const [transition, setTransition] = useState<{
    variant: SuggestTransitionVariant;
    phase: SuggestTransitionPhase;
  } | null>(null);
  const transitionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (transitionTimeoutRef.current) clearTimeout(transitionTimeoutRef.current);
    };
  }, []);

  const releaseTransition = useCallback((afterMs: number) => {
    setTransition((t) => (t ? { ...t, phase: 'out' } : t));
    if (transitionTimeoutRef.current) clearTimeout(transitionTimeoutRef.current);
    transitionTimeoutRef.current = window.setTimeout(() => setTransition(null), afterMs);
  }, []);

  // "Contact Henry" modal (top-bar button → overlay with his email).
  const [showContact, setShowContact] = useState(false);

  // Onboarding tour: null until the trigger conditions below are met (first
  // visit, review mode, not approved, per-script toggle on, not seen yet),
  // then 'gate' (hard-gated, first visit) or 'reentry' (dismissible, opened
  // from the footer's "See the tour again"). Never reset back to null by the
  // trigger effect itself — only onClose (below) does that — so a
  // background script refetch mid-tour can't fight the modal that's open.
  const [tourMode, setTourMode] = useState<'gate' | 'reentry' | null>(null);

  // PDF export runs a lazy chunk load + render, so it can take a beat — the
  // button shows "Preparing your PDF…" and disables while it runs.
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  // Lets the review card's "General notes" action reveal + focus the notes
  // panel further down the page.
  const notesRef = useRef<ClientNotesHandle>(null);
  const notesPendingRef = useRef(0);
  const onNotesPendingChange = useCallback((_slug: string, pending: boolean) => {
    notesPendingRef.current += pending ? 1 : -1;
  }, []);

  const loadScript = useCallback(() => {
    if (!slug) return;
    clientApi
      .getScript(slug)
      .then(setScript)
      .catch((err) => {
        if (err instanceof ApiError && err.status === 410) {
          setError('replaced');
        } else if (err instanceof ApiError && err.status === 404) {
          setError('not_found');
        } else if (err instanceof ApiError && err.status === 423) {
          // Draft/archived — Henry is mid-revision; the server withholds
          // the working copy.
          setError('revising');
        } else {
          setError('error');
        }
      });
  }, [slug]);

  useEffect(() => {
    loadScript();
  }, [loadScript]);

  useEffect(() => {
    if (!script) return;
    document.title = `${script.client_name} · ${script.title} · Sheahan Studios`;
  }, [script]);

  const shootContent = useMemo(
    () => (script ? hasShootView(script.doc_json, script.template_key) : false),
    [script?.doc_json, script?.template_key],
  );
  // The phone card's blurb names what a script's shoot view holds.
  const scriptHasRows = useMemo(
    () => (script ? shootableRows(extractScriptRows(script.doc_json)).length > 0 : false),
    [script?.doc_json],
  );

  // Onboarding tour trigger — only once the script payload has actually
  // loaded (never a flash-then-hide, never a premature show on the
  // skeleton — this does mean one frame of the page can paint before the
  // gate does, an accepted limitation, not a bug), and only while genuinely
  // first-run: review mode, not approved, the per-script toggle on, and the
  // seen key absent. Re-runs on every fresh script load (not just the
  // first), which is deliberate: in a bundle, navigating to another script
  // re-checks the same global seen key rather than a per-mount flag, so an
  // abandoned/incomplete tour on script A still gates script B — abandoning
  // only happens by leaving the page (close/reload), which resets the latch
  // below along with everything else, so this holds for ordinary storage.
  //
  // gateShownThisMount guards the one case that story doesn't cover: a
  // browser where BOTH localStorage and sessionStorage throw (private mode
  // gone stricter, storage disabled). There, hasSeenOnboarding() can never
  // return true, so without this latch the gate would re-slam on every
  // script/mode change within the same visit (e.g. leaving suggest mode, or
  // switching scripts in a bundle) even after the client just finished it.
  // The latch caps it at once per mount — once per visit — restoring
  // onboardingSeen's "hard-gated at most once per session" promise for
  // fully storage-blocked visitors.
  const gateShownThisMountRef = useRef(false);
  useEffect(() => {
    if (!script) return;
    if (mode !== 'review') return;
    if (script.status === 'approved') return;
    if (script.features.onboarding === false) return;
    if (gateShownThisMountRef.current) return;
    if (hasSeenOnboarding()) return;
    gateShownThisMountRef.current = true;
    setTourMode('gate');
  }, [script, mode]);

  // Warn before tab close while suggestions or general notes are unsaved.
  // The suggest-mode autosave debounces, so a fast close after a keystroke
  // could lose the most recent characters. Native browser dialog handles UX.
  useEffect(() => {
    function onBeforeUnload(e: BeforeUnloadEvent) {
      warnBeforeUnload(e, shouldWarnClientBeforeUnload(suggestion.saveState, notesPendingRef.current > 0));
    }
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [suggestion.saveState]);

  // Heartbeat for the "client viewing now" admin indicator. Fires every
  // 30s while the tab is visible — paused when hidden so a backgrounded
  // tab doesn't keep the badge falsely lit. First tick fires immediately
  // on mount so the admin sees presence within 1-2 polls.
  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    async function tick() {
      if (cancelled) return;
      if (document.visibilityState === 'visible') {
        await clientApi.heartbeat(slug!);
      }
    }
    void tick();
    const id = window.setInterval(tick, 30_000);
    const onVis = () => {
      if (document.visibilityState === 'visible') void tick();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [slug]);

  const comments = useScriptComments({
    mode: 'client',
    slug,
    enabled: Boolean(slug && script),
  });

  // Resolved threads only render a highlight when the user opens them from the sidebar.
  const threadAnchors = useMemo<ThreadAnchor[]>(
    () =>
      comments.threads
        .filter((t) => t.status !== 'resolved' || t.id === activeThreadId)
        .map((t) => ({
          id: t.id,
          from: t.anchor_from,
          to: t.anchor_to,
          anchor_text: t.anchor_text,
          status: t.status,
        })),
    [comments.threads, activeThreadId],
  );

  const handleSelectionChange = useCallback((info: SelectionInfo) => {
    setSelection(info);
    if (info.empty) {
      setSelectionRect(null);
      return;
    }
    const docSel = window.getSelection();
    if (docSel && docSel.rangeCount > 0) {
      const rect = docSel.getRangeAt(0).getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        setSelectionRect(rect);
      }
    }
  }, []);

  // Both handlers below call openComposer, which is defined further down
  // (after the loading guards, because it needs the loaded script). That is
  // safe from an event handler; do not call it from the render body.
  function startCommentDraft() {
    if (!selection || selection.empty) return;
    void openComposer({ from: selection.from, to: selection.to, text: selection.text });
  }

  // Tap-to-comment (Feature K): a per-block "+" target opens the same composer
  // anchored to the whole block — the touch/keyboard-friendly path.
  function handleBlockTap(detail: { from: number; to: number; text: string }) {
    setActiveThreadId(null);
    void openComposer(detail);
  }

  function activateThread(threadId: string | null) {
    setActiveThreadId(threadId);
    if (!threadId || !editor) return;
    const t = comments.threads.find((x) => x.id === threadId);
    if (!t) return;
    const docSize = editor.state.doc.content.size;
    const from = Math.max(0, Math.min(t.anchor_from, docSize));
    const to = Math.max(from, Math.min(t.anchor_to, docSize));
    editor.commands.setTextSelection({ from, to });
    editor.commands.scrollIntoView();
  }

  // The review card's "1 · Comment" action — scrolls/focuses the comments
  // column and surfaces a short hint, per R2. Comments themselves are
  // unaffected; this only draws the eye to where they live.
  function focusComments() {
    const el = document.getElementById('cr-comments');
    if (el) {
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
      el.focus({ preventScroll: true });
    }
    showToast(
      'success',
      `Select any text in the ${isBrief ? 'brief' : 'script'}, or tap + on a block, to comment.`,
    );
  }

  async function enterSuggestMode() {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Reduced motion gets the plain instant swap below, unchanged — no
    // overlay, no artificial holds. Anything fancier (e.g. a quick fade)
    // would mean threading the same timing branches through twice for a
    // case that explicitly wants the simplest possible behaviour.
    const startedAt = Date.now();
    if (!reduceMotion) setTransition({ variant: 'enter', phase: 'in' });

    // Acquiring the edit lock is part of entering. start() covers both the
    // no-draft (create) and existing-editing-draft cases; resume() flips a
    // submitted draft back to editing. Either can fail for different
    // reasons — a teammate's fresh lock (409), the script having just been
    // approved out from under this request (403), or something else — and
    // each needs its own copy rather than always assuming a lock fight.
    const res =
      suggestion.suggestion?.status === 'submitted'
        ? await suggestion.resume()
        : await suggestion.start();
    if (!res.ok) {
      // Failure: never strand the client behind the overlay. Dissolve it
      // immediately — no minimum hold — so the error toast is visible
      // right away instead of waiting out a "read the overlay" beat that
      // no longer applies.
      if (!reduceMotion) releaseTransition(200);
      if (res.reason === 'approved') {
        showToast(
          'error',
          `This ${isBrief ? 'brief' : 'script'} has already been approved. Suggested edits are closed.`,
        );
        // The local script/suggestion state is stale relative to the server
        // (something else approved it); refresh so the page catches up to
        // the collapsed approved view instead of quietly staying wrong.
        loadScript();
      } else if (res.reason === 'locked') {
        const who = res.holderName || suggestion.lock.holderName || 'Someone on your team';
        showToast(
          'error',
          `${who} is editing right now. You can take over once they’ve stepped away.`,
        );
      } else {
        showToast('error', 'That didn’t go through. Please try again in a moment.');
      }
      return;
    }

    // Success: hold the overlay for a minimum readable stretch so a fast
    // acquisition doesn't strobe it on and back off — it masks the wait,
    // it shouldn't feel like a flicker.
    if (!reduceMotion) {
      const remaining = 500 - (Date.now() - startedAt);
      if (remaining > 0) await new Promise((resolve) => setTimeout(resolve, remaining));
    }

    setMode('suggest');
    // Utility bar + hero + review card unmount when entering suggest mode;
    // auto-scroll to top so the suggest banner sits cleanly at the top of
    // the viewport with the document right beneath it.
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior }), 30);

    if (!reduceMotion) {
      // Give suggest mode a beat to paint underneath before releasing —
      // the swap happens under the overlay, never in view.
      await new Promise((resolve) => setTimeout(resolve, 90));
      releaseTransition(320);
    }
  }

  // Keep the edit lock alive while in suggest mode without typing (autosave
  // already extends it on each save). Paused when the tab is hidden.
  useEffect(() => {
    if (mode !== 'suggest') return;
    const id = window.setInterval(() => {
      if (document.visibilityState === 'visible') void suggestion.heartbeat();
    }, 30_000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // If the active draft disappears out from under suggest mode — Henry's
  // "Reset client view" discarding it, or an approval closing it — exit back
  // to review cleanly. Without this the client is stranded: the suggest
  // banner sits over a read-only master doc while autosave retries a 404
  // forever. The null can arrive via the 15s poll or via flush()'s own 404
  // handling (useClientSuggestion) — both funnel through here. loadScript()
  // refetches the page state, since an approval changes it.
  useEffect(() => {
    if (mode !== 'suggest' || suggestion.loading || suggestion.suggestion) return;
    setMode('review');
    showToast(
      'error',
      'Suggested edits was just closed from our side, so your draft is no longer active. You’re viewing the latest script.',
    );
    loadScript();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, suggestion.suggestion, suggestion.loading]);

  async function exitSuggestMode() {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Lighter than the entry transition on purpose: a quick fade-through,
    // no minimum hold. Leaving suggest mode is returning to where the
    // client came from, not entering somewhere new — it doesn't earn the
    // same held beat.
    if (!reduceMotion) setTransition({ variant: 'exit', phase: 'in' });

    // Flush any pending autosave before exiting so the latest keystrokes are
    // persisted. The "Send for review" action that follows expects the
    // server-side doc_json to be up to date.
    const saved = await suggestion.flush();
    if (!saved) {
      showToast('error', 'Your latest suggested changes could not be saved. Please retry.');
      if (!reduceMotion) releaseTransition(120);
      return;
    }
    setMode('review');
    // Utility bar + hero come back; scroll to top so the user sees the full
    // restored layout from the start.
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior }), 30);

    if (!reduceMotion) releaseTransition(200);
  }

  async function handleDownloadPdf() {
    if (!script || downloadingPdf) return;
    setDownloadingPdf(true);
    try {
      const input = {
        title: script.title,
        client_name: script.client_name,
        status: script.status,
        approved_at: script.approved_at,
        slug: script.slug,
        doc_json: script.doc_json,
      };
      // The client only ever downloads the client copy. The doc they hold has
      // already been projected by the worker, and the renderer projects again
      // on the way in, so an internal field cannot reach this file.
      if (isBriefTemplate(script.template_key)) {
        await downloadBriefPdf({ ...input, variant: 'client' });
      } else {
        await downloadScriptPdf(input);
      }
    } catch {
      showToast('error', 'That PDF didn’t generate. Please try again in a moment.');
    } finally {
      setDownloadingPdf(false);
    }
  }

  if (error === 'replaced') {
    return (
      <main className="min-h-screen bg-page text-fg flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <Eyebrow accent>// LINK REPLACED</Eyebrow>
          <h1 className="font-display font-bold text-[40px] leading-[1.05] tracking-[-0.02em] mt-3 mb-4">
            This link has been replaced
          </h1>
          <p className="text-muted leading-[1.55]">
            A new review link has been issued for this script. Henry can send it straight over.
          </p>
          <ContactLine className="mt-4" />
        </div>
      </main>
    );
  }

  if (error === 'not_found') {
    return (
      <main className="min-h-screen bg-page text-fg flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <Eyebrow accent>// 404</Eyebrow>
          <h1 className="font-display font-bold text-[40px] leading-[1.05] tracking-[-0.02em] mt-3 mb-4">
            Script not found
          </h1>
          <p className="text-muted leading-[1.55]">
            The link may be incorrect or the script may have been removed. Henry can check and
            send you a fresh link.
          </p>
          <ContactLine className="mt-4" />
        </div>
      </main>
    );
  }

  if (error === 'revising') {
    return (
      <main className="min-h-screen bg-page text-fg flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <Eyebrow accent>// BEING REVISED</Eyebrow>
          <h1 className="font-display font-bold text-[40px] leading-[1.05] tracking-[-0.02em] mt-3 mb-4">
            This script is being revised
          </h1>
          <p className="text-muted leading-[1.55]">
            Henry is working on the next version right now. He’ll send it back shortly. This
            link will work again as soon as it’s ready.
          </p>
          <ContactLine className="mt-4" />
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="min-h-screen bg-page text-fg flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <Eyebrow accent>// SOMETHING WENT WRONG</Eyebrow>
          <h1 className="font-display font-bold text-[40px] leading-[1.05] tracking-[-0.02em] mt-3 mb-4">
            We couldn’t load this script
          </h1>
          <p className="text-muted leading-[1.55]">
            It’s most likely temporary: your connection or a brief hiccup on our end.
          </p>
          <div className="mt-6">
            <Button
              variant="primary"
              glow
              onClick={() => {
                setError(null);
                loadScript();
              }}
            >
              Try again
            </Button>
          </div>
          <ContactLine className="mt-6" />
        </div>
      </main>
    );
  }

  if (!script) {
    return (
      <main className="min-h-screen bg-page">
        <div className="max-w-3xl mx-auto px-6 md:px-12 pt-20 pb-24">
          <div className="h-3 w-32 bg-card rounded-sm mb-8 skeleton" />
          <div className="h-14 w-2/3 bg-card rounded-sm mb-10 skeleton" />
          <div className="space-y-3">
            <div className="h-4 bg-card rounded-sm skeleton" />
            <div className="h-4 bg-card rounded-sm w-5/6 skeleton" />
            <div className="h-4 bg-card rounded-sm w-4/6 skeleton" />
          </div>
        </div>
      </main>
    );
  }

  const masterDoc = parseDocJson(script.doc_json);
  const suggestDoc = parseDocJson(suggestion.suggestion?.doc_json);

  // A master document we can't parse is unrecoverable from the client's side:
  // there is nothing to show, and rendering on would throw somewhere deeper.
  // Point them at Henry rather than at a blank page.
  if (!masterDoc) {
    return (
      <main className="min-h-screen bg-page text-fg flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <Eyebrow accent>// SOMETHING WENT WRONG</Eyebrow>
          <h1 className="font-display font-bold text-[40px] leading-[1.05] tracking-[-0.02em] mt-3 mb-4">
            We couldn’t display this script
          </h1>
          <p className="text-muted leading-[1.55]">
            The script loaded but its contents couldn’t be read. Henry needs to take a look —
            nothing you’ve done caused this.
          </p>
          <ContactLine className="mt-6" />
        </div>
      </main>
    );
  }

  const inSuggestMode = mode === 'suggest';
  const featuresOn = script.features;
  // An interview brief travels the same route, the same workflow and the same
  // card as a script; only the words change (mock-up §06).
  const isBrief = isBriefTemplate(script.template_key);
  const hasActiveDraft =
    !!suggestion.suggestion &&
    (suggestion.suggestion.status === 'editing' ||
      suggestion.suggestion.status === 'submitted');

  // The doc shown to viewers. While there's an active draft (whether someone
  // is mid-edit or pending review), other clients see the working draft so the
  // team can review/iterate together. Comments are hidden during a draft.
  // An unreadable draft falls back to master — showing the approved copy beats
  // showing nothing, and master is always parseable by the guard above.
  const showingDraft = (inSuggestMode || hasActiveDraft) && !!suggestDoc;
  const displayDoc = showingDraft ? suggestDoc! : masterDoc;

  // Open the comment composer for a selection, recording which document the
  // positions belong to at this moment rather than when Post is pressed:
  // the poll can swap the document underneath an open composer, and the
  // server must then refuse the old positions. In suggest mode the reviewer
  // may have typed since the last autosave, so the draft is saved first and
  // the version that save produced is the one recorded.
  const openComposer = async (range: { from: number; to: number; text: string }) => {
    if (hasActiveDraft && !suggestDoc) {
      showToast('error', 'Comments are paused while a suggested edit that cannot be shown is open. They come back once that edit has been reviewed.');
      return;
    }
    let suggestionEditVersion: number | null = null;
    if (showingDraft) {
      if (inSuggestMode) {
        // A save that failed (too large, lock lost) or one still queued means
        // the words on screen are not the words the server holds, so there
        // is nothing safe to anchor to yet.
        const saved = await suggestion.flush();
        if (!saved || !suggestion.draftIsSaved()) {
          showToast('error', 'Your latest suggested changes have not been saved yet, so a comment cannot be placed. Once they have saved, select the text again.');
          return;
        }
        suggestionEditVersion = suggestion.latestEditVersion();
      } else {
        suggestionEditVersion = suggestion.suggestion!.edit_version;
      }
    }
    setDraftAnchor({
      ...range,
      doc_version: script.doc_version,
      suggestion_id: showingDraft ? suggestion.suggestion!.id : null,
      suggestion_edit_version: suggestionEditVersion,
    });
    setSelectionRect(null);
  };

  // The comment poll reports the server's current doc_version; if it has
  // moved past the version this tab loaded, Henry has published changes and
  // the copy on screen is stale.
  const scriptStale =
    comments.scriptDocVersion !== null &&
    comments.scriptDocVersion > script.doc_version;
  // 2A: the cue keys off publishes, never autosaves. A new round names the
  // version; a quiet update keeps today's wording.
  const newRoundSince =
    comments.scriptRound !== null && script.published_round !== null && comments.scriptRound > script.published_round
      ? comments.scriptRound
      : null;
  // A quiet update landed while the team was mid-suggestion: their draft
  // keeps its base, and the banner says so.
  const updatedWhileEditing =
    !!suggestion.suggestion &&
    newRoundSince === null &&
    (comments.scriptDocVersion ?? script.doc_version) > suggestion.suggestion.based_on_version;

  return (
    <main data-context="client" className="min-h-screen bg-page text-fg">
      <Toaster />
      <RefImageLightbox />
      {transition && <SuggestModeTransition variant={transition.variant} phase={transition.phase} />}
      {showContact && <ContactModal onClose={() => setShowContact(false)} />}
      {tourMode && (
        <ClientOnboardingTour
          dismissible={tourMode === 'reentry'}
          skippable={script.features.onboarding_skip}
          kind={isBrief ? 'brief' : 'script'}
          onClose={() => setTourMode(null)}
        />
      )}

      {/* Utility bar — wordmark, bundle script picker (when bundle-wrapped),
          and the secondary utilities (Contact Henry / Download PDF / Shoot
          view). In-flow now, not absolute — no magic hero clearance to
          maintain. Hidden in suggest mode (the sticky suggest banner takes
          over as the single top-of-viewport control surface). Becomes a
          static, print-friendly header in the printed output. */}
      {!inSuggestMode && (
        <header data-print-keep className="border-b border-line">
          <div className="max-w-7xl mx-auto px-4 md:px-12 min-h-14 py-2.5 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
            {/* md:flex-1 gives this group a zero flex basis, so the row's wrap
                decision never counts the picker: the picker shrinks and its
                title truncates instead of pushing the utilities down a line. */}
            <div className="flex items-center gap-3 min-w-0 md:flex-1">
              <Wordmark size="sm" className="shrink-0" />
              {bundleReview && (
                <BundleScriptPicker
                  bundleReview={bundleReview}
                  currentSlug={script.slug}
                  currentScript={{ status: script.status, feedback_submitted_at: script.feedback_submitted_at }}
                />
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2" data-print-hide>
              {script && shootContent && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => openShootView(`/s/${script.slug}/interview`, navigate, { forceNewTab: !!bundleReview })}
                  title={
                    isBrief
                      ? 'Phone-friendly view of the messaging and questions for shoot day'
                      : 'Phone-friendly view of the script for shoot day'
                  }
                >
                  Shoot view
                </Button>
              )}
              {script && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleDownloadPdf}
                  disabled={downloadingPdf}
                  title="Download branded PDF"
                  aria-label={downloadingPdf ? 'Preparing your PDF' : 'Download PDF'}
                >
                  {downloadingPdf ? (
                    <>
                      <span className="md:hidden">Preparing…</span>
                      <span className="hidden md:inline">Preparing your PDF…</span>
                    </>
                  ) : (
                    <>
                      <span className="md:hidden">PDF</span>
                      <span className="hidden md:inline">Download PDF</span>
                    </>
                  )}
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowContact(true)}
                title="Get in touch with Henry directly"
                aria-label="Contact Henry"
              >
                <span className="md:hidden">Contact</span>
                <span className="hidden md:inline">Contact Henry</span>
              </Button>
            </div>
          </div>
        </header>
      )}

      {/* Hero — compact: eyebrow, "Prepared for", title, and the four-stage
          tracker on the left; the compact "what changed" card on the right.
          Hidden in suggest mode for focus. */}
      {!inSuggestMode && (
        <section className="border-b border-line">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 md:py-8">
            <div className="flex flex-col md:flex-row md:items-start justify-between gap-5 md:gap-8">
              <div className="min-w-0 flex-1">
                <div
                  className="flex items-start justify-between gap-4 animate-rise-subtle"
                  style={{ animationDelay: '0ms' }}
                >
                  <Eyebrow accent>
                    {isBrief
                      ? script.status === 'approved'
                        ? '// INTERVIEW BRIEF · APPROVED'
                        : '// INTERVIEW BRIEF · PREPARED FOR REVIEW'
                      : script.status === 'approved'
                      ? '// SCRIPT · APPROVED'
                      : '// SCRIPT · PREPARED FOR REVIEW'}
                  </Eyebrow>
                  {script.status === 'approved' && (
                    <ApprovedBadge approvedAt={script.approved_at} />
                  )}
                </div>
                <p
                  className="text-muted text-[12px] uppercase font-mono tracking-[0.08em] mt-3 animate-rise-subtle"
                  style={{ animationDelay: '100ms' }}
                >
                  Prepared for <span className="text-fg">{script.client_name}</span>
                </p>
                <h1
                  className="font-display font-bold leading-[1.05] tracking-[-0.015em] mt-2 animate-rise"
                  style={{
                    fontSize: 'clamp(1.6rem, 3.2vw, 2.2rem)',
                    animationDelay: '200ms',
                  }}
                >
                  {script.title}
                </h1>
                {script.status === 'approved' && (
                  <p
                    className="text-muted text-[14px] leading-[1.55] mt-3 max-w-prose animate-rise-subtle"
                    style={{ animationDelay: '250ms' }}
                  >
                    {isBrief
                      ? 'This brief has been approved. Sheahan Studios is now moving it into production.'
                      : 'This script has been approved. Sheahan Studios is now moving it into production.'}
                  </p>
                )}
                {/* D9: a quiet update after approval says so, so the page
                    never silently differs from the PDF signed off. */}
                {script.status === 'approved' && script.tidied && (
                  <p className="text-faint text-[12.5px] leading-[1.5] mt-1.5 max-w-prose">
                    Tidied on {formatTidiedDate(script.tidied.at)}: {script.tidied.note}
                  </p>
                )}
                <div className="animate-rise-subtle" style={{ animationDelay: '300ms' }}>
                  <ProgressTracker status={script.status} publishedRound={script.published_round} kind={isBrief ? 'brief' : 'script'} />
                </div>
              </div>
              {/* Only rendered when there's actually a summary to show —
                  otherwise this wrapper's flex gap would reserve dead space
                  next to the tracker for nothing. */}
              {hasRoundSummary(script.round_summary, script.status) && (
                <div className="shrink-0 animate-rise-subtle" style={{ animationDelay: '300ms' }}>
                  <WhatChangedBanner roundSummary={script.round_summary} status={script.status} />
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Suggest mode sticky bar — the always-reachable control surface
          while the client team is editing. Carries the save indicator and
          Done editing button so neither requires scrolling back to the top. */}
      {inSuggestMode && (
        <div className="suggest-banner animate-fade-in" data-print-hide>
          {/* min-h + wrap (not fixed h-14): on phones the longer states
              ("Save failed" + Retry) must wrap to a second row rather than
              overlap the eyebrow. */}
          <div className="max-w-7xl mx-auto px-6 md:px-12 min-h-14 py-2 md:py-0 flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
            <div className="flex items-center gap-4 min-w-0">
              <span className="font-mono text-[10px] uppercase tracking-[0.12em] font-medium text-accent shrink-0">
                // Suggest mode
              </span>
              {updatedWhileEditing && !suggestion.lock.lockLost && !suggestion.teammateEditing ? (
                <span className="text-accent text-[13px] leading-snug truncate">
                  Henry updated v{script.published_round ?? 1} while you were editing. Your edits are kept.
                </span>
              ) : suggestion.lock.lockLost ? (
                <span className="text-warning text-[13px] leading-snug truncate">
                  <strong className="font-semibold">
                    {suggestion.lock.holderName || 'Someone'}
                  </strong>{' '}
                  took over. Your recent changes weren’t saved.
                </span>
              ) : suggestion.teammateEditing ? (
                <span className="text-accent text-[13px] leading-snug truncate">
                  Someone on your team is also editing. The latest save wins.
                </span>
              ) : (
                <span className="hidden md:inline text-muted text-[13px] leading-snug truncate">
                  Edit the {isBrief ? 'brief' : 'script'} directly. Your team can review and send it for review together.
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <SuggestSaveIndicator
                state={suggestion.saveState}
                onRetry={() => void suggestion.retrySave()}
              />
              <Button variant="primary" size="sm" glow onClick={exitSuggestMode}>
                Done editing
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* The "Your review" card — full-width, between the hero and the
          document. Absorbs the old status card's finish-line CTAs plus the
          three numbered review actions that used to live in the header. */}
      {!inSuggestMode && (
        <ClientReviewCard
          slug={script.slug}
          kind={isBrief ? 'brief' : 'script'}
          scriptStatus={script.status}
          publishedRound={script.published_round}
          approveBy={script.approve_by}
          approvedAt={script.approved_at}
          suggestionStatus={suggestion.suggestion?.status ?? null}
          suggestionLoading={suggestion.loading}
          feedbackSubmittedAt={script.feedback_submitted_at}
          feedbackSubmittedBy={script.feedback_submitted_by}
          commentsEnabled={featuresOn.comments}
          suggestionsEnabled={featuresOn.suggestions}
          onEnterSuggestMode={enterSuggestMode}
          onCommentClick={focusComments}
          onLeaveGeneralNote={() => notesRef.current?.open()}
          onAfterSubmit={async () => {
            // Refresh the suggestion (server may have promoted it from
            // editing → submitted) so the page state stays in sync.
            await suggestion.refresh();
          }}
          onBeforeSubmit={async (authorName, note) => {
            if (notesRef.current && !await notesRef.current.flush(authorName)) {
              showToast('error', 'Your latest general notes could not be saved. Please retry before sending feedback.');
              throw new Error('General notes are still unsaved');
            }
            if (suggestion.suggestion?.status === 'editing') {
              await suggestion.submit({ author_name: authorName, note });
            }
          }}
          onAfterApprove={async () => {
            // Re-fetch the script so status flips to 'approved' in the
            // UI immediately — the editor goes read-only, the card
            // shows the approved view. Also refresh the suggestion: the
            // server closes out any in-flight draft on approval
            // (closeSuggestionOnApproval), and refreshing here means
            // hasActiveDraft/displayDoc pick that up immediately instead
            // of waiting for the next 15s poll — the approved page must
            // always show the canonical document, never a stale draft.
            if (slug) {
              const fresh = await clientApi.getScript(slug);
              setScript(fresh);
              await suggestion.refresh();
            }
          }}
          title={script.title}
          clientName={script.client_name}
          dashboardSlug={script.dashboard_slug}
        />
      )}

      {/* C8: "What we need from you", under the review card so Approve
          stays where it is. The server sends null when it's off, outside
          v1 to v3, or a brief that isn't switched on. */}
      {!inSuggestMode && script.review_checklist && script.review_checklist.length > 0 && (
        <div className="max-w-7xl mx-auto px-6 md:px-12 mt-4">
          <ReviewChecklistCard
            prompts={script.review_checklist}
            slug={script.slug}
            status={script.status}
          />
        </div>
      )}

      {/* Phones only: the header button is one of three in a wrapping row
          at this width, so the offer is repeated once where the thumb
          already is (mock-up §06, screen 1). Briefs and scripts alike. */}
      {!inSuggestMode && shootContent && (
        <div className="md:hidden max-w-7xl mx-auto px-6 mt-4" data-print-hide>
          <ShootViewCard
            blurb={
              isBrief
                ? 'The messaging and the questions, sized for a phone.'
                : scriptHasRows
                  ? 'Every line and shot, sized for a phone.'
                  : 'The interview questions, sized for a phone.'
            }
            onOpen={() => openShootView(`/s/${script.slug}/interview`, navigate, { forceNewTab: !!bundleReview })}
          />
        </div>
      )}

      {!inSuggestMode && bundleReview && script.status === 'approved' && (
        <div className="max-w-7xl mx-auto px-6 md:px-12 mt-4 md:mt-6">
          <BundleFollowOn context={bundleReview} currentSlug={script.slug} />
        </div>
      )}

      {/* Stale-tab cue and working-draft banner — full-width, above the grid
          (not inside its left column), so the doc + comments columns start
          level with each other regardless of whether these render. */}
      {!inSuggestMode && (scriptStale || newRoundSince !== null) && (
        <div className="max-w-7xl mx-auto px-6 md:px-12 mt-4 md:mt-6">
          <div
            data-print-hide
            className="border border-accent bg-accent/5 rounded-md px-4 py-3 flex items-center justify-between gap-4 animate-rise-subtle"
          >
            <p className="text-[13px] text-fg leading-snug">
              {newRoundSince !== null
                ? `Henry published version ${newRoundSince} since you opened this page.`
                : `This ${isBrief ? 'brief' : 'script'} was updated since you opened it.`}
            </p>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                loadScript();
                void suggestion.refresh();
              }}
            >
              {newRoundSince !== null ? `See v${newRoundSince}` : 'Refresh'}
            </Button>
          </div>
        </div>
      )}

      {!inSuggestMode && hasActiveDraft && suggestDoc && (
        <div className="max-w-7xl mx-auto px-6 md:px-12 mt-4 md:mt-6">
          <div data-print-hide className="border border-line bg-elevated rounded-md px-4 py-3 animate-rise-subtle">
            <p className="text-[13px] text-muted leading-snug">
              You’re viewing your team’s edited draft, not the original version Henry sent.
            </p>
            {suggestion.lock.lockedByOther && (
              <p className="text-[13px] text-accent leading-snug mt-1">
                <strong className="font-semibold">
                  {suggestion.lock.holderName || 'Someone on your team'}
                </strong>{' '}
                is editing right now. You can take over once they’ve stepped away.
              </p>
            )}
            {!suggestion.lock.lockedByOther && suggestion.lock.staleTakeoverAvailable && (
              <div className="flex items-center justify-between gap-3 mt-2 flex-wrap">
                <p className="text-[13px] text-warning leading-snug">
                  <strong className="font-semibold">
                    {suggestion.lock.holderName || 'Someone on your team'}
                  </strong>{' '}
                  was editing but seems to have stepped away.
                </p>
                <Button variant="secondary" size="sm" onClick={enterSuggestMode}>
                  Take over
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Document + sidebar — comments are the sidebar's sole occupant now,
          top-aligned with the document card. The two-column track only
          exists when the sidebar actually renders (featuresOn.comments) —
          otherwise the doc goes full-width instead of leaving a dead
          340px-wide gap where a hidden aside would have been. */}
      <section
        className={`cr-doc-grid max-w-7xl mx-auto px-6 md:px-12 py-8 md:py-10 grid grid-cols-1 gap-6 md:gap-8 ${
          featuresOn.comments ? 'md:grid-cols-[1fr_340px]' : ''
        }`}
      >
        <div className="min-w-0">
          <div
            className="bg-card border border-line rounded-md px-6 md:px-12 py-10 md:py-14 animate-fade-in"
            style={{ animationDelay: '120ms' }}
          >
            {/* On-camera runtime of the audio column — now inside the doc
                card (top-right) so the card's outer border sits exactly
                level with the comments column's, which the top-alignment
                rule depends on. Recomputes live as the client edits in
                suggest mode. */}
            <div data-print-hide className="flex justify-end items-center flex-wrap gap-x-3 gap-y-2 min-h-[1.25rem] mb-2">
              <RuntimeBadge editor={editor} />
              {/* Read-through (2B): scripts only, never in suggest mode. */}
              {!isBrief && !inSuggestMode && <ReadThrough editor={editor} />}
            </div>
            {inSuggestMode && suggestDoc ? (
              // Editable, but still a client's view of the document: the same
              // data-brief-view wrapper keeps the serif field titles, and
              // briefControls={false} keeps the audience chip and ⋯ menu out
              // of it. No data-readonly here, since the body genuinely is
              // typeable.
              <div data-brief-view="client">
                <Editor
                  key={`suggest-${suggestion.suggestion?.id}`}
                  initialDoc={suggestDoc}
                  onUpdate={(doc) => suggestion.scheduleAutosave(JSON.stringify(doc))}
                  editable={true}
                  briefControls={false}
                  onReady={setEditor}
                  onSelectionChange={featuresOn.comments ? handleSelectionChange : undefined}
                  threads={featuresOn.comments ? threadAnchors : undefined}
                  activeThreadId={activeThreadId}
                />
              </div>
            ) : (
              // data-brief-view scopes the client presentation of brief fields
              // (serif section titles, styles.css) to this page only; the admin
              // version preview shares data-readonly but keeps the editor look.
              // briefControls={false} is redundant while this is read-only, and
              // kept so that "no client-facing editor builds the chip or the ⋯
              // menu" holds by construction rather than by read-only-ness.
              <div data-readonly="true" data-brief-view="client">
                <Editor
                  key={
                    hasActiveDraft
                      ? `draft-${suggestion.suggestion?.id}-${suggestion.suggestion?.updated_at}`
                      : `master-${script.slug}-v${script.doc_version}`
                  }
                  initialDoc={displayDoc}
                  onUpdate={() => {}}
                  editable={false}
                  briefControls={false}
                  onReady={setEditor}
                  onSelectionChange={handleSelectionChange}
                  threads={threadAnchors}
                  activeThreadId={activeThreadId}
                  blockComments={featuresOn.comments}
                  onBlockTap={handleBlockTap}
                />
              </div>
            )}
          </div>

          {!inSuggestMode && (
            <ClientNotesPanel
              key={script.slug}
              ref={notesRef}
              onPendingChange={onNotesPendingChange}
              mode="client"
              slug={script.slug}
              kind={isBrief ? 'brief' : 'script'}
              commentsEnabled={featuresOn.comments}
            />
          )}

          <div className="mt-12" data-print-hide>
            <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
              <ContactLine />
              {!inSuggestMode && featuresOn.onboarding && (
                <button
                  type="button"
                  onClick={() => setTourMode('reentry')}
                  className="
                    inline-flex items-center gap-1.5 shrink-0
                    font-mono text-[11px] uppercase tracking-[0.08em] font-semibold
                    text-accent hover:opacity-80 transition-opacity
                  "
                >
                  <span aria-hidden>↻</span> See the tour again
                </button>
              )}
            </div>
            <div className="flex items-center justify-between text-faint">
              <span className="font-mono text-[11px] uppercase tracking-[0.12em]">
                Sheahan Studios · Confidential
              </span>
              <span className="font-mono text-[11px] uppercase tracking-[0.12em]">
                // {script.slug}
              </span>
            </div>
          </div>
        </div>

        {featuresOn.comments && (
          <CommentSidebar
            mode="client"
            kind={isBrief ? 'brief' : 'script'}
            slug={slug}
            threads={comments.threads}
            loading={comments.loading}
            draftAnchor={draftAnchor}
            onCancelDraft={() => setDraftAnchor(null)}
            onCreateThread={async (params) => {
              // The versions captured when the composer opened, never the
              // page's current ones: those may already describe a newer
              // document than the one the positions were selected in.
              const captured = draftAnchor;
              if (!captured || captured.stale) {
                throw new Error('Select the text again to place this comment.');
              }
              try {
                await comments.createThread({
                  thread_id: params.thread_id,
                  anchor_text: params.anchor_text,
                  anchor_from: params.anchor_from,
                  anchor_to: params.anchor_to,
                  author_name: params.author_name,
                  body: params.body,
                  doc_version: captured.doc_version,
                  suggestion_id: captured.suggestion_id,
                  suggestion_edit_version: captured.suggestion_edit_version,
                });
              } catch (err) {
                if (err instanceof ApiError && err.status === 409) {
                  // Bring the page up to the document the server now holds
                  // and mark the anchor dead: the composer keeps the words
                  // typed so far, the selection pill comes back, and a fresh
                  // selection re-arms Post against the current document.
                  setDraftAnchor((a) => (a ? { ...a, stale: true } : a));
                  comments.refresh();
                  loadScript();
                  void suggestion.refresh();
                }
                throw err;
              }
            }}
            onReply={async (threadId, body, author_name) => {
              await comments.reply(threadId, { thread_id: threadId, body, author_name });
            }}
            onResolve={comments.resolve}
            onReopen={comments.reopen}
            onActivateThread={activateThread}
            activeThreadId={activeThreadId}
          />
        )}
      </section>

      {featuresOn.comments && (
        <SelectionPill rect={draftAnchor && !draftAnchor.stale ? null : selectionRect} onAdd={startCommentDraft} />
      )}
    </main>
  );
}

function SuggestSaveIndicator({
  state,
  onRetry,
}: {
  state: SuggestSaveState;
  onRetry: () => void;
}) {
  if (state === 'clean') return null;
  let label = '';
  let dot = 'bg-faint/40';
  switch (state) {
    case 'dirty':
      label = 'Editing';
      dot = 'bg-accent animate-pulse';
      break;
    case 'saving':
      label = 'Saving';
      dot = 'bg-accent animate-pulse';
      break;
    case 'saved':
      label = 'Saved';
      dot = 'bg-accent';
      break;
    case 'error':
      label = 'Save failed';
      dot = 'bg-accent';
      break;
  }
  // Always visible — on phones especially, "Save failed" being hidden means
  // a client can lose an edit without ever knowing.
  return (
    <span className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.12em] text-muted mr-2">
      <span className={`w-1.5 h-1.5 rounded-full ${dot}`} aria-hidden />
      {label}
      {state === 'error' && (
        <button
          type="button"
          onClick={onRetry}
          className="text-accent underline underline-offset-2 hover:opacity-80 transition-opacity"
        >
          Retry
        </button>
      )}
    </span>
  );
}

/**
 * Bundle-only post-approval follow-on (Phase 3). Offers "Next script" (the
 * next unapproved ready script after this one, in bundle order) and "Back to
 * all scripts". Only ever rendered when BundleReviewContext is present, so
 * standalone /s/:slug never mounts this.
 */
function BundleFollowOn({
  context,
  currentSlug,
}: {
  context: BundleReviewContextValue;
  currentSlug: string;
}) {
  const navigate = useNavigate();
  const currentIndex = context.items.findIndex((i) => i.slug === currentSlug);
  const next =
    currentIndex === -1
      ? undefined
      : context.items.slice(currentIndex + 1).find((i) => i.status !== 'approved');

  return (
    <div data-print-hide className="flex flex-wrap items-center gap-3">
      {next && (
        <Button
          variant="primary"
          size="sm"
          glow
          onClick={() => navigate(`/b/${context.bundleSlug}/${next.slug}`)}
        >
          Next script → {next.title}
        </Button>
      )}
      <Button
        variant={next ? 'secondary' : 'primary'}
        size="sm"
        glow={!next}
        onClick={() => navigate(`/b/${context.bundleSlug}`)}
      >
        Back to all scripts
      </Button>
    </div>
  );
}

function ApprovedBadge({ approvedAt }: { approvedAt: number | null }) {
  const dateLabel = approvedAt
    ? new Date(approvedAt).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : 'recently';
  return (
    <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent/10 border border-accent text-accent shrink-0 animate-fade-in">
      <span className="w-1.5 h-1.5 rounded-full bg-accent" aria-hidden />
      <span className="font-mono text-[11px] uppercase tracking-[0.12em] font-medium">
        Approved · {dateLabel}
      </span>
    </span>
  );
}

/** "Fri 13 Nov" for the tidied line (D9). */
function formatTidiedDate(at: number): string {
  return new Date(at)
    .toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })
    .replace(',', '');
}
